import React from "react";
import { Box, FormHelperText } from "@mui/material";
import { Field } from "react-final-form";
import CheckboxRow from "../shared/CheckboxRow";
import RffInputWrapper from "../shared/RffInputWrapper";
import normalizeOptions from "../../../utils/finalForm/normalizeOptions";

/**
 * RffCheckboxArrayField — "select all that apply" as a vertical checklist rather
 * than a multi-select dropdown, for questions where the options should be
 * readable without opening a menu.
 *
 * The stored value is the same ARRAY a multi-select produces, so this is a
 * drop-in swap for RffSelectMultipleField with no change to the wire contract.
 * An empty selection stores `undefined`, not `[]`, so "nothing ticked" and
 * "never touched" look the same to a downstream `isEmpty` check.
 */
const RffCheckboxArrayField = ({
  name,
  label,
  options: rawOptions = [],
  required = false,
  helperText,
  suppressGrid,
  size,
  disabled,
  gap = 1.25,
  // Options read as a vertical checklist by default; set 2+ when the list is
  // long enough that one column pushes the next question off screen.
  columns = 1,
}) => {
  const options = normalizeOptions(rawOptions);

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
    >
      <Field
        name={name}
        validate={(v) =>
          required && (!Array.isArray(v) || v.length === 0)
            ? "Select at least one option"
            : undefined
        }
        render={({ input, meta }) => {
          // final-form hands back "" for an untouched field, not [].
          const selected = Array.isArray(input.value) ? input.value : [];
          const toggle = (value, checked) => {
            const next = checked
              ? [...selected, value]
              : selected.filter((v) => v !== value);
            input.onChange(next.length > 0 ? next : undefined);
          };

          return (
            <Box
              sx={{
                display: "grid",
                // Always one column below sm — two half-width rows on a phone
                // squeeze the longer labels onto three lines each.
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: `repeat(${columns}, minmax(0, 1fr))`,
                },
                gap,
              }}
            >
              {options.map((option) => (
                <CheckboxRow
                  key={option.value}
                  label={option.label}
                  disabled={disabled}
                  checked={selected.includes(option.value)}
                  onChange={(checked) => toggle(option.value, checked)}
                />
              ))}
              {/* Spans the grid so the message sits under the whole list, not
                  under whichever option landed in the last column. */}
              {meta.touched && meta.error && (
                <FormHelperText error sx={{ gridColumn: "1 / -1", mx: 0 }}>
                  {meta.error}
                </FormHelperText>
              )}
            </Box>
          );
        }}
      />
    </RffInputWrapper>
  );
};

export default RffCheckboxArrayField;
